import React, { useState, useRef, useEffect } from 'react';
import { 
  ShoppingBag, 
  Search, 
  Menu, 
  X, 
  Leaf, 
  Shield, 
  UserCheck, 
  PhoneCall, 
  Sparkles,
  ExternalLink,
  MapPin
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';
import { useCart } from '../../context/CartContext';
import { formatPrice, formatMizajDisplay } from '../../utils/formatters';
import { searchProducts } from '../../utils/searchHelper';
import { getProductAltText } from '../../utils/seoHelpers';

const Navbar = ({ activeTab, setActiveTab, onOpenAdminLogin, onSelectProduct }) => {
  const { lang, t, isRtl } = useLanguage();
  const { settings, products, mizajList } = useStore();
  const { totalItemsCount, setIsCartOpen, setSelectedProductModal, setSelectedProduct, viewProductDetail } = useCart();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const desktopSearchRef = useRef(null);
  const mobileSearchRef = useRef(null);

  const handleSelectSearchResult = (prod) => {
    if (onSelectProduct) {
      onSelectProduct(prod);
    } else {
      if (viewProductDetail) {
        viewProductDetail(prod);
      }
      if (setSelectedProduct) {
        setSelectedProduct(prod);
      }
      setActiveTab('product-detail');
    }
    setIsSearchFocused(false);
    setSearchQuery('');
    setMobileMenuOpen(false);
    if (desktopSearchRef.current) {
      const input = desktopSearchRef.current.querySelector('input');
      if (input) input.blur();
    }
    if (mobileSearchRef.current) {
      const input = mobileSearchRef.current.querySelector('input');
      if (input) input.blur();
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Close search dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inDesktop = desktopSearchRef.current && desktopSearchRef.current.contains(event.target);
      const inMobile = mobileSearchRef.current && mobileSearchRef.current.contains(event.target);
      if (!inDesktop && !inMobile) {
        setIsSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtered products for live search with intelligent Urdu/English matching & synonyms
  const searchResults = searchQuery.trim() === '' ? [] : searchProducts(products, searchQuery, 8);

  const handleNavClick = (tabId) => {
    setActiveTab(tabId);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navLinks = [
    { id: 'home', label: t('home') },
    { id: 'shop', label: t('shop') },
    { id: 'categories', label: t('categories') },
    { id: 'blogs', label: lang === 'ur' ? 'بلاگ و مقالات' : 'Blog & Articles' },
    { id: 'tibbi-guide', label: t('tibbiGuide') },
    { id: 'about-hakim', label: t('aboutHakim') },
    { id: 'contact', label: lang === 'ur' ? 'رابطہ و مطب' : 'Contact & Clinic' },
  ];

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100 transition-all">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          
          {/* Logo & Brand Identity */}
          <div 
            onClick={() => handleNavClick('home')}
            className="flex items-center gap-3 cursor-pointer group shrink-0"
          >
            {settings?.logoUrl ? (
              <div className="w-11 h-11 sm:w-13 sm:h-13 rounded-2xl overflow-hidden border border-gray-100 shadow-md group-hover:scale-105 transition-transform duration-300 bg-white flex items-center justify-center p-1">
                <img src={settings.logoUrl} alt={settings.storeNameUrdu || "Store Logo"} className="w-full h-full object-contain" />
              </div>
            ) : (
              <div className="w-11 h-11 sm:w-13 sm:h-13 rounded-2xl bg-gradient-to-br from-pansar-700 via-pansar-800 to-pansar-950 flex items-center justify-center text-amber-400 shadow-md group-hover:scale-105 transition-transform duration-300">
                <Leaf className="w-6 h-6 sm:w-7 sm:h-7" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-lg sm:text-2xl text-pansar-900 tracking-tight font-title">
                  {lang === 'ur' ? settings.storeNameUrdu : settings.storeNameEn}
                </span>
                <span className="hidden sm:inline-block px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[11px] font-semibold font-subheading">
                  خالص پنسار
                </span>
              </div>
              <p className="text-xs sm:text-xs text-gray-500 line-clamp-1 font-subheading font-medium">
                {lang === 'ur' ? settings.dawakhanaNameUrdu : settings.dawakhanaNameEn}
              </p>
            </div>
          </div>

          {/* Live Search Bar (Desktop) */}
          <div className="hidden lg:block flex-1 max-w-lg mx-4 relative" ref={desktopSearchRef}>
            <div className="relative">
              <input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setIsSearchFocused(true);
                }}
                onFocus={() => setIsSearchFocused(true)}
                onClick={() => setIsSearchFocused(true)}
                className={`w-full py-2.5 px-10 text-sm bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-pansar-600 focus:bg-white transition-all shadow-inner ${
                  lang === 'ur' ? 'font-urdu text-right' : 'font-sans text-left'
                }`}
              />
              <Search className={`w-4 h-4 text-gray-400 absolute top-1/2 -translate-y-1/2 ${
                lang === 'ur' ? 'left-3.5' : 'right-3.5'
              }`} />
              {searchQuery && (
                <button 
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setIsSearchFocused(false);
                  }}
                  className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs bg-gray-200 rounded-full w-5 h-5 flex items-center justify-center cursor-pointer ${
                    lang === 'ur' ? 'right-3.5' : 'left-3.5'
                  }`}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Live Search Results Dropdown */}
            {isSearchFocused && searchQuery.trim() !== '' && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-[100] animate-fade-in max-h-96 overflow-y-auto">
                <div className="p-2 border-b border-gray-100 bg-gray-50 text-xs text-gray-500 font-medium flex justify-between">
                  <span>{t('showingResults')} {searchResults.length}</span>
                  <span className="truncate max-w-[150px]">{searchQuery}</span>
                </div>
                {searchResults.length > 0 ? (
                  searchResults.map(product => (
                    <div
                      key={product.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectSearchResult(product);
                      }}
                      onTouchStart={(e) => {
                        handleSelectSearchResult(product);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectSearchResult(product);
                      }}
                      className="p-3 hover:bg-pansar-50 active:bg-pansar-100 transition cursor-pointer flex items-center gap-3 border-b border-gray-50 last:border-0 select-none"
                    >
                      <img 
                        src={product.image || product.images?.[0] || 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=200&auto=format&fit=crop&q=80'} 
                        alt={getProductAltText(product, 0, lang)} 
                        className="w-12 h-12 rounded-lg object-cover shrink-0 border border-gray-200"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm text-gray-900 truncate font-urdu">
                          {lang === 'ur' ? product.nameUrdu : product.nameEn}
                        </h4>
                        <p className="text-xs text-emerald-700 font-medium font-urdu">
                          {formatMizajDisplay(product, mizajList, lang)}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="text-sm font-bold text-pansar-800 font-sans">
                          {formatPrice(product.variants?.[0]?.price || product.price || 0, lang)}
                        </span>
                        <div className="text-[10px] text-gray-400 font-urdu">
                          {product.variants?.[0]?.weight || ''}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-6 text-center text-sm text-gray-500 font-urdu">
                    {t('noProductsFound')}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Action Icons: WhatsApp Chat, Cart, Admin */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Clinic Location -> navigates to contact tab */}
            <button
              type="button"
              onClick={() => handleNavClick('contact')}
              className="hidden md:flex items-center gap-2 px-3.5 py-2 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-900 font-bold text-xs transition border border-emerald-300 cursor-pointer shadow-2xs"
              title="دواخانہ کا پتہ اور گوگل لوکیشن"
            >
              <MapPin className="w-3.5 h-3.5 text-emerald-700" />
              <span className="font-urdu">دواخانہ لوکیشن و پتہ</span>
            </button>

            {/* Shopping Cart Trigger */}
            <button
              onClick={() => setIsCartOpen(true)}
              className="relative p-2.5 rounded-full bg-pansar-50 hover:bg-pansar-100 text-pansar-800 transition border border-pansar-200 focus:outline-none focus:ring-2 focus:ring-pansar-500"
              aria-label="Cart"
            >
              <ShoppingBag className="w-5 h-5 text-pansar-800" />
              {totalItemsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-amber-500 text-slate-950 text-[11px] font-black w-5 h-5 rounded-full flex items-center justify-center shadow-md animate-bounce">
                  {totalItemsCount}
                </span>
              )}
            </button>

            {/* Admin Login Button */}
            <button
              onClick={onOpenAdminLogin}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition"
              title={t('adminLogin')}
            >
              <Shield className="w-5 h-5" />
            </button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-xl text-gray-700 hover:bg-gray-100 transition lg:hidden"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Search Bar */}
        <div className="mt-3 lg:hidden relative" ref={mobileSearchRef}>
          <div className="relative">
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setIsSearchFocused(true);
              }}
              onFocus={() => setIsSearchFocused(true)}
              onClick={() => setIsSearchFocused(true)}
              className={`w-full py-2 px-9 text-xs bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-pansar-600 ${
                lang === 'ur' ? 'font-urdu text-right' : 'font-sans text-left'
              }`}
            />
            <Search className={`w-4 h-4 text-gray-400 absolute top-1/2 -translate-y-1/2 ${
              lang === 'ur' ? 'left-3' : 'right-3'
            }`} />
            {searchQuery && (
              <button 
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setIsSearchFocused(false);
                }}
                className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs bg-gray-200 rounded-full w-4 h-4 flex items-center justify-center cursor-pointer ${
                  lang === 'ur' ? 'right-3' : 'left-3'
                }`}
              >
                ✕
              </button>
            )}
          </div>
          {/* Mobile search dropdown */}
          {isSearchFocused && searchQuery.trim() !== '' && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-2xl border border-gray-100 z-[100] max-h-72 overflow-y-auto">
              <div className="p-2 border-b border-gray-100 bg-gray-50 text-[11px] text-gray-500 font-medium flex justify-between font-urdu">
                <span>{t('showingResults')} {searchResults.length}</span>
                <span className="truncate max-w-[120px]">{searchQuery}</span>
              </div>
              {searchResults.length > 0 ? (
                searchResults.map(product => (
                  <div
                    key={product.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectSearchResult(product);
                    }}
                    onTouchStart={(e) => {
                      handleSelectSearchResult(product);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectSearchResult(product);
                    }}
                    className="p-2.5 hover:bg-gray-50 active:bg-gray-100 transition cursor-pointer flex items-center gap-2.5 border-b border-gray-50 last:border-0 select-none"
                  >
                    <img 
                      src={product.image || product.images?.[0] || 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=200&auto=format&fit=crop&q=80'} 
                      alt={getProductAltText(product, 0, lang)} 
                      className="w-10 h-10 rounded-md object-cover" 
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-xs text-gray-900 truncate font-urdu">
                        {lang === 'ur' ? product.nameUrdu : product.nameEn}
                      </p>
                      <span className="text-[11px] font-bold text-pansar-700 font-sans">
                        {formatPrice(product.variants?.[0]?.price || product.price || 0, lang)}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-xs text-gray-500 font-urdu">
                  {t('noProductsFound')}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Desktop Navigation Links */}
        <nav className="hidden lg:flex items-center justify-center gap-8 mt-2.5 pt-2 border-t border-gray-100">
          {navLinks.map(link => (
            <button
              key={link.id}
              onClick={() => handleNavClick(link.id)}
              className={`text-sm font-medium transition-colors relative py-1 font-urdu ${
                activeTab === link.id
                  ? 'text-pansar-700 font-bold'
                  : 'text-gray-600 hover:text-pansar-800'
              }`}
            >
              {link.label}
              {activeTab === link.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-pansar-700 rounded-full" />
              )}
            </button>
          ))}
        </nav>

        {/* Mobile Navigation Drawer Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-3 pt-3 border-t border-gray-100 flex flex-col gap-2 animate-fade-in pb-2">
            {navLinks.map(link => (
              <button
                key={link.id}
                onClick={() => handleNavClick(link.id)}
                className={`text-right py-2 px-3 rounded-lg text-sm font-medium font-urdu transition ${
                  activeTab === link.id
                    ? 'bg-pansar-100 text-pansar-900 font-bold'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {link.label}
              </button>
            ))}
            <div className="pt-2 border-t border-gray-100 flex items-center justify-between px-3 text-xs text-gray-500">
              <span className="font-urdu">حکیم مولانا سید عبدالوہاب شاہ شیرازی</span>
              <a
                href={`tel:${settings.phone}`}
                className="text-emerald-700 font-bold hover:underline dir-ltr"
                title="فون کال کریں"
              >
                {settings.phone}
              </a>
            </div>
          </div>
        )}

      </div>
    </header>
  );
};

export default Navbar;
